import React, { useEffect, useRef } from 'react'
import styles from './IdCardForm.module.scss'

function csrfToken() {
  const el = document.querySelector('meta[name="csrf-token"]')
  return el ? el.getAttribute('content') : ''
}

// Pick the verb based on the actual file the finisher published.
// Single-chunk jobs ship a .pdf; multi-chunk jobs ship a .zip.
function downloadLabel(url) {
  if (!url) return 'Download'
  const path = url.split('?')[0].toLowerCase()
  if (path.endsWith('.zip')) return 'Download Zip'
  if (path.endsWith('.pdf')) return 'Download PDF'
  return 'Download'
}

function bannerStyle(bg, color) {
  return {
    marginTop: 10, padding: '10px 14px', borderRadius: 5,
    background: bg, border: '1px solid #ddd', fontSize: 14, color
  }
}

const dismissStyle = {
  marginLeft: 10, background: 'transparent', border: 'none', color: '#888', cursor: 'pointer'
}

export function CsvImportButton({ templateId, csvInfo, setCsvInfo, onImported, busy }) {
  const inputRef = useRef(null)

  async function handleFileChange(e) {
    const file = e.target.files && e.target.files[0]
    e.target.value = ''
    if (!file) return

    const formData = new FormData()
    formData.append('csv', file)

    setCsvInfo({ ...csvInfo, uploading: true, error: null })

    try {
      const res = await fetch(`/new_templates/${templateId}/import_csv`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken() },
        body: formData,
      })
      const data = await res.json()
      if (!res.ok) {
        setCsvInfo({ ...csvInfo, uploading: false, error: data.error || 'Upload failed' })
        return
      }
      setCsvInfo({ ...data, uploading: false, error: null })
      if (onImported) onImported(data)
    } catch (err) {
      setCsvInfo({ ...csvInfo, uploading: false, error: err.message })
    }
  }

  const uploading = csvInfo && csvInfo.uploading
  return (
    <>
      <input
        type="file"
        accept=".csv,text/csv"
        ref={inputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <button
        type="button"
        className={styles.pdfButton}
        disabled={uploading || busy}
        style={{ background: (uploading || busy) ? '#888' : '#5cb85c', border: 'none', cursor: (uploading || busy) ? 'default' : 'pointer' }}
        onClick={() => inputRef.current && inputRef.current.click()}
      >
        {uploading ? 'Uploading…' : 'Import CSV'}
      </button>
    </>
  )
}

export function CsvSourceBanner({ templateId, csvInfo, setCsvInfo, onCleared, busy }) {
  if (!csvInfo) return null

  if (csvInfo.error) {
    return (
      <div style={bannerStyle('#fdecea', '#c33')}>
        <span>Error: {csvInfo.error}</span>
        <button onClick={() => setCsvInfo(null)} style={dismissStyle}>✕</button>
      </div>
    )
  }

  if (!csvInfo.loaded) return null

  async function handleClear() {
    if (busy) return
    if (!window.confirm('Clear the imported CSV and revert to default data? Any fields you placed for CSV columns will also be removed.')) return
    try {
      const res = await fetch(`/new_templates/${templateId}/clear_csv`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrfToken() },
      })
      const data = await res.json()
      setCsvInfo(data)
      if (onCleared) onCleared()
    } catch (err) {
      setCsvInfo({ ...csvInfo, error: err.message })
    }
  }

  return (
    <div style={bannerStyle('#eef7ee', '#2a7a2a')}>
      <span>
        <strong>Active data source:</strong>{' '}
        <code>{csvInfo.filename}</code> ({csvInfo.row_count} {csvInfo.row_count === 1 ? 'row' : 'rows'})
      </span>
      <button
        onClick={handleClear}
        disabled={busy}
        style={{
          marginLeft: 12, padding: '3px 10px', borderRadius: 4,
          background: '#fff', border: '1px solid #c33', color: '#c33',
          cursor: busy ? 'default' : 'pointer', fontSize: 12,
        }}
      >
        Clear / Use default data
      </button>
    </div>
  )
}

export function PdfButton({ templateId, csvInfo, syncPdfUrl, pdfJob, setPdfJob, testMode, setTestMode }) {
  const generating = pdfJob && ['queued', 'processing'].includes(pdfJob.status)
  const csvLoaded = csvInfo && csvInfo.loaded

  // No CSV loaded → existing sync PDF link (opens in new tab)
  if (!csvLoaded) {
    return (
      <a
        href={syncPdfUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.pdfButton}
      >
        PDF
      </a>
    )
  }

  async function handleGenerate() {
    if (generating) return
    setPdfJob({ status: 'queued', total: testMode ? 1 : csvInfo.row_count, processed: 0 })
    try {
      const body = new FormData()
      if (testMode) body.append('test_mode', '1')
      const res = await fetch(`/new_templates/${templateId}/generate_csv_pdfs`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken() },
        body,
      })
      const data = await res.json()
      if (!res.ok) {
        setPdfJob({ status: 'error', error: data.error || 'Failed to start PDF generation' })
        return
      }
      setPdfJob({ jobId: data.job_id, status: 'queued', total: data.total, processed: 0 })
    } catch (err) {
      setPdfJob({ status: 'error', error: err.message })
    }
  }

  return (
    <>
      <button
        type="button"
        className={styles.pdfButton}
        disabled={generating}
        onClick={handleGenerate}
        style={{ cursor: generating ? 'default' : 'pointer' }}
      >
        {generating ? 'Generating…' : 'PDF'}
      </button>
      <label
        title="Generate only the first row instead of the entire CSV"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 8,
          fontSize: 13, fontWeight: 'normal', cursor: 'pointer', userSelect: 'none'
        }}
      >
        <input
          type="checkbox"
          checked={!!testMode}
          onChange={(e) => setTestMode(e.target.checked)}
          disabled={generating}
          style={{ margin: 0 }}
        />
        Test
      </label>
    </>
  )
}

export function PdfJobStatus({ templateId, pdfJob, setPdfJob, onDismiss }) {
  const jobId = pdfJob && pdfJob.jobId
  const isActive = pdfJob && ['queued', 'processing'].includes(pdfJob.status)

  useEffect(() => {
    if (!jobId || !isActive) return

    let cancelled = false
    const tick = async () => {
      try {
        const res = await fetch(`/new_templates/${templateId}/csv_export_status?job_id=${jobId}`)
        if (!res.ok) return
        const data = await res.json()
        if (cancelled) return
        setPdfJob({
          jobId,
          status: data.status,
          total: data.total,
          processed: data.processed,
          pdfUrl: data.pdf_url,
          error: data.error,
        })
      } catch (_) {}
    }
    const interval = setInterval(tick, 2000)
    return () => { cancelled = true; clearInterval(interval) }
  }, [jobId, isActive, templateId])

  if (!pdfJob) return null

  const percent = pdfJob.total > 0 ? Math.round((pdfJob.processed / pdfJob.total) * 100) : 0

  return (
    <div style={bannerStyle('#f7f7f9', '#333')}>
      {pdfJob.status === 'queued' && <span>Queued — waiting for worker…</span>}
      {pdfJob.status === 'processing' && (
        <span>Generating PDF: {pdfJob.processed} of {pdfJob.total} ({percent}%)</span>
      )}
      {pdfJob.status === 'complete' && (
        <>
          <span style={{ color: '#2a7a2a', marginRight: 12 }}>✓ Complete ({pdfJob.total} rows)</span>
          <a href={pdfJob.pdfUrl} target="_blank" rel="noopener noreferrer"
             style={{ color: '#337ab7', textDecoration: 'underline' }}>
            {downloadLabel(pdfJob.pdfUrl)}
          </a>
          <button onClick={onDismiss} style={dismissStyle}>✕</button>
        </>
      )}
      {pdfJob.status === 'error' && (
        <>
          <span style={{ color: '#c33' }}>Error: {pdfJob.error}</span>
          <button onClick={onDismiss} style={dismissStyle}>✕</button>
        </>
      )}
    </div>
  )
}
